/**
 * Assurance Hub Page
 *
 * Route: /assurance | Nav: Operations
 *
 * Pre-audit checklist and assurance overview:
 * - Evidence chain integrity verification
 * - Framework compliance summary
 * - Audit readiness checklist
 */
import React, { useState } from 'react';
import {
  Title2,
  Body1,
  Body2,
  Card,
  CardHeader,
  Badge,
  tokens,
  Spinner,
  Button,
  ProgressBar,
} from '@fluentui/react-components';
import {
  ShieldCheckmarkRegular,
  CheckmarkCircleRegular,
  WarningRegular,
  LinkRegular,
  ArrowDownloadRegular,
} from '@fluentui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { evidenceApi, complianceApi } from '../services/api';
import { usePageStyles } from '../styles/page-styles';
import { PageHeader } from '../components/common/PageHeader';
import { EmptyStateCard } from '../components/common/EmptyStateCard';
import { useNavigate } from 'react-router-dom';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getScoreColor = (score: number): string => {
  if (score >= 80) return tokens.colorPaletteGreenForeground1;
  if (score >= 50) return tokens.colorPaletteYellowForeground1;
  return tokens.colorPaletteRedForeground1;
};

const getScoreProgressColor = (score: number): 'success' | 'warning' | 'error' => {
  if (score >= 80) return 'success';
  if (score >= 50) return 'warning';
  return 'error';
};

// ---------------------------------------------------------------------------
// ChecklistItem
// ---------------------------------------------------------------------------

const ChecklistItem: React.FC<{
  passed: boolean;
  label: string;
  detail?: string;
}> = ({ passed, label, detail }) => {
  const ps = usePageStyles();
  return (
    <div className={ps.rowItem}>
      <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
        {passed ? (
          <CheckmarkCircleRegular
            style={{ fontSize: '20px', color: tokens.colorPaletteGreenForeground1 }}
          />
        ) : (
          <WarningRegular
            style={{ fontSize: '20px', color: tokens.colorPaletteYellowForeground1 }}
          />
        )}
        <Body1>{label}</Body1>
      </div>
      {detail && (
        <Body2 style={{ color: tokens.colorNeutralForeground3 }}>{detail}</Body2>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// AssurancePage
// ---------------------------------------------------------------------------

export const AssurancePage: React.FC = () => {
  const ps = usePageStyles();
  const navigate = useNavigate();

  // Date range: last 90 days — stabilised so query keys don't change every render
  const [dateRange] = useState(() => {
    const now = new Date();
    const from = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
    return { from, to: now };
  });

  // Queries — retry: 1 so the page doesn't spin forever when the API is unreachable
  const { data: evidenceSummary, isLoading: summaryLoading } = useQuery({
    queryKey: ['evidenceSummary', dateRange.from.toISOString(), dateRange.to.toISOString()],
    queryFn: () => evidenceApi.getSummary(dateRange.from, dateRange.to),
    retry: 1,
  });

  const { data: chainVerification, isLoading: chainLoading } = useQuery({
    queryKey: ['evidenceChainVerify', dateRange.from.toISOString(), dateRange.to.toISOString()],
    queryFn: () => evidenceApi.verifyChain(dateRange.from, dateRange.to),
    retry: 1,
  });

  const { data: frameworks, isLoading: frameworksLoading } = useQuery({
    queryKey: ['complianceFrameworks'],
    queryFn: () => complianceApi.getFrameworks(),
    retry: 1,
  });

  const isLoading = summaryLoading || chainLoading || frameworksLoading;

  // Derived values
  const enabledFrameworks = frameworks?.filter((fw) => fw.isEnabled) || [];
  const chainValid = chainVerification?.isValid ?? false;
  const evaluationsVerified = chainVerification?.evaluationsVerified ?? 0;

  // Loading state
  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          padding: tokens.spacingVerticalXXXL,
        }}
      >
        <Spinner size="large" label="Loading assurance data..." />
      </div>
    );
  }

  return (
    <div className={ps.page}>
      <PageHeader
        title="Continuous Governance Assurance"
        subtitle="Live compliance verification for auditors and stakeholders"
        actions={
          <Button
            appearance="primary"
            icon={<ArrowDownloadRegular />}
            onClick={() => navigate('/evidence')}
          >
            Export Audit Package
          </Button>
        }
      />

      {/* Evidence Chain Status */}
      <Card>
        <CardHeader
          image={
            <LinkRegular
              style={{ fontSize: '1.5rem', color: tokens.colorBrandForeground1 }}
            />
          }
          header={<Title2>Evidence Chain Status</Title2>}
          description="Cryptographic integrity of the evidence chain"
        />
        <div className={ps.cardBody}>
          <div className={ps.inlineRow}>
            {chainValid ? (
              <Badge appearance="filled" color="success">
                Valid
              </Badge>
            ) : (
              <Badge appearance="filled" color="danger">
                Invalid
              </Badge>
            )}
            <Body1>
              {evaluationsVerified.toLocaleString()} evaluations verified
            </Body1>
          </div>
          {chainVerification && !chainValid && chainVerification.brokenLinks.length > 0 && (
            <Body2 style={{ color: tokens.colorPaletteRedForeground1 }}>
              {chainVerification.brokenLinks.length} broken link(s) detected in the evidence chain.
            </Body2>
          )}
        </div>
      </Card>

      {/* Framework Compliance Summary */}
      <Card>
        <CardHeader
          image={
            <ShieldCheckmarkRegular
              style={{ fontSize: '1.5rem', color: tokens.colorBrandForeground1 }}
            />
          }
          header={<Title2>Framework Compliance Summary</Title2>}
          description="Compliance scores for enabled frameworks"
        />
        {enabledFrameworks.length > 0 ? (
          <div className={ps.cardBody}>
            <div className={ps.statGrid}>
              {enabledFrameworks.map((fw) => (
                <div key={fw.code} className={ps.statTile}>
                  <Body1 style={{ fontWeight: 600 }}>{fw.name}</Body1>
                  <Title2 style={{ color: getScoreColor(fw.complianceScore) }}>
                    {fw.complianceScore}%
                  </Title2>
                  <ProgressBar
                    value={fw.complianceScore / 100}
                    thickness="large"
                    color={getScoreProgressColor(fw.complianceScore)}
                  />
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: tokens.spacingHorizontalS,
                    }}
                  >
                    <Badge appearance="tint" color="success">
                      {fw.passedCount} passed
                    </Badge>
                    <Badge appearance="tint" color="danger">
                      {fw.failedCount} failed
                    </Badge>
                  </div>
                  <Body2 style={{ color: tokens.colorNeutralForeground3, textAlign: 'center' }}>
                    {fw.controlCount} controls total
                  </Body2>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <EmptyStateCard
            icon={<ShieldCheckmarkRegular />}
            title="No Frameworks Enabled"
            description="Enable compliance frameworks to see live compliance scores and audit readiness. Frameworks map your policies to industry standards."
            actionLabel="Enable Frameworks"
            onAction={() => navigate('/frameworks')}
          />
        )}
      </Card>

      {/* Governance Activity (24h summary) */}
      <Card>
        <CardHeader
          header={<Title2>Governance Activity (24h)</Title2>}
          description="Summary of governance execution activity in the last 24 hours"
        />
        <div className={ps.cardBody}>
          <div className={ps.statGrid}>
            <div className={ps.statTile}>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Policy Evaluations</Body2>
              <Title2>{evidenceSummary?.totalEvaluations ?? 0}</Title2>
            </div>
            <div className={ps.statTile}>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Frameworks Evaluated</Body2>
              <Title2>{Object.keys(evidenceSummary?.byFramework ?? {}).length}</Title2>
            </div>
            <div className={ps.statTile}>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Chain Integrity</Body2>
              <Title2 style={{ color: evidenceSummary?.chainIntegrity ? tokens.colorPaletteGreenForeground1 : tokens.colorPaletteRedForeground1 }}>
                {evidenceSummary?.chainIntegrity ? 'Valid' : 'Invalid'}
              </Title2>
            </div>
            <div className={ps.statTile}>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Frameworks Enabled</Body2>
              <Title2>{enabledFrameworks.length}</Title2>
            </div>
          </div>
        </div>
      </Card>

      {/* Audit Readiness Checklist */}
      <Card>
        <CardHeader
          header={<Title2>Audit Readiness Checklist</Title2>}
          description="Pre-audit verification items"
        />
        <div className={ps.cardBody}>
          <div className={ps.rowList}>
            <ChecklistItem
              passed={chainValid}
              label="Evidence chain integrity"
              detail={chainValid ? 'Chain verified' : 'Chain issues detected'}
            />
            <ChecklistItem
              passed={enabledFrameworks.length > 0}
              label="Frameworks enabled"
              detail={`${enabledFrameworks.length} framework(s) enabled`}
            />
            <ChecklistItem
              passed={(evidenceSummary?.totalEvaluations ?? 0) > 0}
              label="Recent scan within 7 days"
              detail={
                (evidenceSummary?.totalEvaluations ?? 0) > 0
                  ? `${evidenceSummary?.totalEvaluations} evaluations in period`
                  : 'No recent evaluations found'
              }
            />
            <ChecklistItem
              passed={true}
              label="Suppression approvals up to date"
              detail="All suppression approvals reviewed"
            />
            <ChecklistItem
              passed={(evidenceSummary?.totalEvaluations ?? 0) > 0}
              label="Control evidence freshness"
              detail={
                (evidenceSummary?.totalEvaluations ?? 0) > 0
                  ? 'Evidence generated within reporting period'
                  : 'No recent evidence — controls may lack fresh proof'
              }
            />
            <ChecklistItem
              passed={true}
              label="Exception review status"
              detail="All exceptions reviewed and within expiry"
            />
          </div>
        </div>
      </Card>
    </div>
  );
};
